import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  Plus,
  Search,
  Edit3,
  Trash2,
  Loader2,
  AlertCircle,
  Package,
  Filter
} from 'lucide-react';
import { 
  type AdminFormularyItem as FormularyListItem
} from '@/services/adminService';
import type { 
  FormularyQueryParams,
  CategoryEnum,
  RouteEnum,
  FormEnum,
  FormularyPharmacy
} from '@/services/formularyService';
import { MultiSelect } from '@/components/MultiSelect';
import type { AppDispatch } from '@/store/store';
import {
  fetchAdminFormularies,
  fetchAdminPharmacies,
  selectAdminFormularies,
  selectAdminPharmacies,
  selectAdminLoading,
  selectAdminError
} from '@/store/slices/adminSlice';
import EditFormularyModal from '@/components/modals/formulary/EditFormularyModal';
import CreateFormularyModal from '@/components/modals/formulary/CreateFormularyModal';


const FormularyManager = () => {
  const dispatch = useDispatch<AppDispatch>();
  
  // Redux selectors
  const formularies = useSelector(selectAdminFormularies);
  const availablePharmacies = useSelector(selectAdminPharmacies);
  const loading = useSelector(selectAdminLoading);
  const error = useSelector(selectAdminError);
  
  // Local state for UI only
  const [localError, setLocalError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<CategoryEnum[]>([]);
  const [selectedRoutes, setSelectedRoutes] = useState<RouteEnum[]>([]);
  const [selectedForms, setSelectedForms] = useState<FormEnum[]>([]);
  const [selectedPharmacies, setSelectedPharmacies] = useState<string[]>([]);
  
  // Dialog states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFormulary, setEditingFormulary] = useState<FormularyListItem | null>(null);

  // Load formularies using Redux - only when filters actually change
  useEffect(() => {
    const params: FormularyQueryParams = {};
    if (searchTerm) params.search = [searchTerm];
    if (selectedCategories.length > 0) params.category = selectedCategories;
    if (selectedRoutes.length > 0) params.route = selectedRoutes;
    if (selectedForms.length > 0) params.form = selectedForms;
    if (selectedPharmacies.length > 0) params.pharmacy = selectedPharmacies;
    
    dispatch(fetchAdminFormularies(params));
  }, [dispatch, searchTerm, selectedCategories, selectedRoutes, selectedForms, selectedPharmacies]);

  // Load pharmacies only once on component mount
  useEffect(() => {
    dispatch(fetchAdminPharmacies());
  }, [dispatch]);

  // Handle create
  const handleCreate = () => {
    // Reload data - trigger a fresh fetch
    dispatch(fetchAdminFormularies());
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    try {
      setLocalError(null);
      await adminApi.deleteFormulary(id);
      dispatch(fetchAdminFormularies());
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to delete formulary');
    }
  };

  // Handle edit
  const handleEdit = (formulary: FormularyListItem) => {
    setEditingFormulary(formulary);
    setShowEditModal(true);
  };

  // Get price range for display
  const getPriceRange = (pharmacies: any[]): string => {
    if (!pharmacies || pharmacies.length === 0) return 'N/A';
    
    const prices = pharmacies.map(p => p.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    
    if (minPrice === maxPrice) {
      return `$${minPrice.toFixed(2)}`;
    }
    
    return `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
  };

  // Get medication names display
  const getMedicationNames = (item: FormularyListItem): string => {
    // FormularyListItem doesn't have formula details, just show name
    return item.name || 'N/A';
  };

  // Get full quantity display
  const getFullQuantityDisplay = (item: FormularyListItem): string => {
    // FormularyListItem has limited info, show form and route
    return `${item.form} - ${item.route}`;
  };



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-syne font-medium text-vitl-navy mb-2">Formulary Management</h2>
          <p className="text-vitl-dark/70 font-lato">Manage medication formularies and their details</p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-lato font-medium"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Formulary
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="border-l-4 border-l-emerald-500 shadow-sm">
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-vitl-dark/50" />
              <Input
                placeholder="Search formularies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 font-lato"
              />
            </div>
            
            {/* Filters - matching Catalogue layout */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <MultiSelect
                options={[
                  { value: "peptide", label: "Peptide" },
                  { value: "weight_loss", label: "Weight Loss" },
                  { value: "hormone", label: "Hormone" },
                  { value: "sexual_health", label: "Sexual Health" },
                  { value: "nootropic", label: "Nootropic" },
                  { value: "anti_aging", label: "Anti-Aging" },
                  { value: "vitamin_replacement", label: "Vitamin Replacement" }
                ]}
                selectedValues={selectedCategories}
                onSelectionChange={setSelectedCategories}
                placeholder="Category"
              />

              <MultiSelect
                options={[
                  { value: "oral", label: "Oral" },
                  { value: "topical", label: "Topical" },
                  { value: "injection", label: "Injection" },
                  { value: "nasal_spray", label: "Nasal Spray" },
                  { value: "suppository", label: "Suppository" }
                ]}
                selectedValues={selectedRoutes}
                onSelectionChange={setSelectedRoutes}
                placeholder="Route"
              />

              <MultiSelect
                options={[
                  { value: "injection", label: "Injection" },
                  { value: "tablet", label: "Tablet" },
                  { value: "capsule", label: "Capsule" },
                  { value: "cream", label: "Cream" },
                  { value: "gel", label: "Gel" }
                ]}
                selectedValues={selectedForms}
                onSelectionChange={setSelectedForms}
                placeholder="Form"
              />

              <MultiSelect
                options={[
                  { value: "Beaker", label: "Beaker" },
                  { value: "Partell", label: "Partell" },
                  { value: "PerfectRx", label: "PerfectRx" },
                  { value: "Promise", label: "Promise" },
                  { value: "Striker", label: "Striker" },
                  { value: "Strive", label: "Strive" }
                ]}
                selectedValues={selectedPharmacies}
                onSelectionChange={setSelectedPharmacies}
                placeholder="Pharmacy"
              />
              
              <div></div> {/* Empty space */}
              
              <div className="flex items-center text-sm text-vitl-dark/70 font-lato">
                <Package className="w-4 h-4 mr-2" />
                {formularies.length} formularies
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
            <p className="text-red-700 font-lato text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      )}

      {/* Formularies Table */}
      {!loading && (
        <Card className="border-l-4 border-l-emerald-500 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-syne font-medium text-vitl-navy flex items-center">
              <Package className="w-5 h-5 mr-2 text-emerald-600" />
              Formulary Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            {formularies.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-vitl-dark/30 mx-auto mb-4" />
                <p className="text-vitl-dark/70 font-lato">No formularies found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {formularies.map((formulary) => (
                  <Card key={formulary.id} className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-all duration-200 shadow-emerald-100/50">
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
                        <div className="lg:col-span-4">
                          <h3 className="font-lato font-medium text-vitl-navy text-lg mb-1">
                            {getMedicationNames(formulary)}
                          </h3>
                          <p className="text-vitl-dark/70 font-lato text-sm font-medium">{getFullQuantityDisplay(formulary)}</p>
                          <p className="text-vitl-dark/50 font-lato text-xs">{formulary.form}</p>
                        </div>
                        
                        <div className="lg:col-span-1 flex items-center">
                          <span className="inline-block px-2 py-1 bg-emerald-50 text-emerald-700 text-xs font-lato rounded border border-emerald-200 mr-4">
                            {formulary.category}
                          </span>
                        </div>

                        <div className="lg:col-span-1">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Route</p>
                            <p className="text-vitl-navy font-lato text-sm">{formulary.route}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Price Range</p>
                            <p className="text-vitl-navy font-lato text-sm font-medium">{getPriceRange(formulary.pharmacies || [])}</p>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato">Available Pharmacies</p>
                            <p className="text-vitl-navy font-lato text-sm">
                              {formulary.pharmacies && formulary.pharmacies.length > 0 ? (
                                formulary.pharmacies.map((pharmacy, idx) => (
                                  <span key={pharmacy.id}>
                                    <span className="whitespace-nowrap">{pharmacy.name}</span>
                                    {idx < (formulary.pharmacies?.length || 0) - 1 && ', '}
                                  </span>
                                ))
                              ) : 'N/A'}
                            </p>
                          </div>
                        </div>

                        <div className="lg:col-span-2 flex items-center justify-end space-x-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(formulary)}
                            className="font-lato"
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 font-lato"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle className="font-syne font-medium text-vitl-navy">
                                  Delete Formulary
                                </AlertDialogTitle>
                                <AlertDialogDescription className="font-lato text-vitl-dark/70">
                                  Are you sure you want to delete "{getMedicationNames(formulary)}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="font-lato">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(formulary.id)}
                                  className="bg-red-600 hover:bg-red-700 font-lato"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modals */}
      <CreateFormularyModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreate}
      />
      <EditFormularyModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingFormulary(null);
        }}
        formulary={editingFormulary}
        onUpdate={() => dispatch(fetchAdminFormularies())}
      />
    </div>
  );
};

export default FormularyManager;